// Self-issue-report helpers (#65). Extracted from HelpPanel so tests can
// exercise validation, context-building, and the submit pipeline without a
// DOM-event framework — the rest of the webapp's tests are SSR-only and we
// don't want to introduce @testing-library/react just for one component.

import { getRecentErrors } from './diagnostics.js'

export const MAX_DESCRIPTION = 2000
export const MAX_FILES = 5
// Per-file cap stays at 10 MB; the cumulative cap is 25 MB (#149) so a
// typical Mac screenshot batch (5 × ~5 MB) doesn't trip the friendly 413.
export const MAX_FILE_BYTES = 10 * 1024 * 1024
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024
export const ALLOWED_MIME_PREFIXES = ['image/']
export const ALLOWED_MIME_EXACT = ['application/pdf', 'text/plain']

export function isAllowedMime(type) {
  if (!type) return false
  if (ALLOWED_MIME_EXACT.includes(type)) return true
  return ALLOWED_MIME_PREFIXES.some((p) => type.startsWith(p))
}

// Returns { ok: true, files } or { ok: false, error: { title, message } }
// for an attempted-add of `incoming` to the existing `current` list.
export function validateAddFiles(current, incoming) {
  const next = [...current]
  for (const f of incoming) {
    if (!isAllowedMime(f.type)) {
      return {
        ok: false,
        error: {
          title: 'File type not allowed',
          message: `${f.name || 'file'} (${f.type || 'unknown'}) is not allowed. Accepted: images, PDF, plain text.`,
        },
      }
    }
    if (next.length >= MAX_FILES) {
      return {
        ok: false,
        error: { title: 'Too many files', message: `Max ${MAX_FILES} files.` },
      }
    }
    if (f.size > MAX_FILE_BYTES) {
      return {
        ok: false,
        error: { title: 'File too large', message: `Each file must be under 10 MB.` },
      }
    }
    const newTotal = next.reduce((s, x) => s + x.size, 0) + f.size
    if (newTotal > MAX_TOTAL_BYTES) {
      return {
        ok: false,
        error: { title: 'Attachments too large', message: `Total over 25.0 MB.` },
      }
    }
    next.push(f)
  }
  return { ok: true, files: next }
}

export function buildSessionContext({ me, route, serverHealth }) {
  const viewport = typeof window !== 'undefined'
    ? { width: window.innerWidth, height: window.innerHeight }
    : null
  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown'
  return {
    appVersion,
    route,
    viewport,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    user: me
      ? { userId: me.userId, email: me.email, role: me.role, displayName: me.displayName }
      : { anonymous: true },
    recentErrors: getRecentErrors(10),
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
    server: serverHealth,
  }
}

// POSTs the multipart payload to /api/v1/self-report. Returns
// { ok: true, issueUrl, reportId } or { ok: false, error: { title, message } }.
// `fetcher` is injectable for tests; defaults to global fetch.
export async function submitReport({ description, files = [], context, fetcher = fetch }) {
  const fd = new FormData()
  fd.append('description', description)
  fd.append('context', JSON.stringify(context))
  for (const f of files) fd.append('attachment', f, f.name)
  let res
  try {
    res = await fetcher('/api/v1/self-report', { method: 'POST', body: fd })
  } catch (err) {
    return {
      ok: false,
      error: { title: 'Could not submit report', message: String(err?.message || err) },
    }
  }
  let body = null
  try { body = await res.json() } catch { /* non-JSON */ }
  if (!res.ok) {
    // 413 typically comes from the nginx gateway (Cahya) before the
    // request even hits Spring — Spring is sized for 10 MB / 12 MB at
    // svc/src/main/resources/application.properties:87-88, but the
    // gateway's `client_max_body_size` is the practical ceiling. Surface
    // a friendly message rather than the raw status (#126).
    if (res.status === 413) {
      return {
        ok: false,
        error: {
          title: 'Attachment too large',
          message: 'The attachments exceeded the server limit. Try removing or shrinking files and resubmit.',
        },
      }
    }
    return {
      ok: false,
      error: {
        title: 'Could not submit report',
        message: body?.message || `HTTP ${res.status}`,
      },
    }
  }
  return { ok: true, issueUrl: body?.issueUrl, reportId: body?.reportId }
}

// Extract attached files from a paste event (returns [] if none are files).
export function filesFromClipboard(clipboardData) {
  if (!clipboardData) return []
  const items = Array.from(clipboardData.items || [])
  return items
    .filter((it) => it.kind === 'file')
    .map((it) => it.getAsFile())
    .filter(Boolean)
}
